import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import SEOHelmet from '../components/SEOHelmet';

const CreditDistributionGuide: React.FC = () => {
  usePageTitle('Credit Distribution Guide');
  const [activeTab, setActiveTab] = useState<'lawyers' | 'architects' | 'engineers'>('lawyers');
  const [openFaq, setOpenFaq] = useState<string | null>('faq1');

  const toggleFaq = (id: string) => setOpenFaq(prev => prev === id ? null : id);

  return (
    <>
      <SEOHelmet
        title="How to Distribute Credits Across Multiple Categories - CEC Track"
        description="Learn how to split continuing education credits across multiple categories. Perfect for lawyers with CLE ethics/diversity requirements, architects with HSW categories, and engineers with PDH requirements."
        keywords="credit distribution, CLE categories, split credits, ethics credits, diversity credits, continuing education categories, multi-category tracking"
        canonical="https://cectrack.com/how-it-works/credit-distribution"
      />

      {/* ── Hero ── */}
      <section style={{ background: 'var(--color-primary)', padding: '3.5rem 0 3rem' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <div className="row align-items-center g-5">
            <div className="col-lg-7">
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-accent)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '0.5rem' }}>
                Credit Distribution Guide
              </p>
              <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(2.5rem, 5vw, 4.5rem)', color: '#fff', lineHeight: 1.05, marginBottom: '1rem', letterSpacing: '-0.01em' }}>
                Distribute Credits Across Multiple Categories
              </h1>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-md)', color: 'rgba(255,255,255,0.65)', maxWidth: '520px', marginBottom: '2rem', lineHeight: 1.65 }}>
                Split a single class's credits across multiple requirement categories for precise compliance tracking.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                {[
                  { abbr: 'LAW', label: 'Attorneys (CLE)',   color: 'var(--color-prof-law)'  },
                  { abbr: 'ARCH', label: 'Architects (LU)',  color: 'var(--color-prof-arch)' },
                  { abbr: 'PE',   label: 'Engineers (PDH)',  color: 'var(--color-prof-pe)'   },
                ].map(p => (
                  <div key={p.abbr} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.875rem', borderRadius: 'var(--radius-full)', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-bold)', color: 'var(--color-accent)', letterSpacing: '0.08em' }}>{p.abbr}</span>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.75)' }}>{p.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-lg-5">
              <div style={{ background: 'var(--color-surface-raised)', borderRadius: 'var(--radius-lg)', padding: '1.75rem', boxShadow: 'var(--shadow-lg)' }}>
                <p style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-bold)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '1rem' }}>
                  Example
                </p>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text)', marginBottom: '0.25rem' }}>NY CLE Class: 3.5 hrs total</p>
                <div style={{ height: '1px', background: 'var(--color-border)', margin: '0.75rem 0' }} />
                <DistRow label="Ethics (Cyber)" amount="2.5 hrs" color="var(--color-prof-law)" />
                <DistRow label="Diversity"      amount="1.0 hr"  color="var(--color-success)"  />
                <div style={{ height: '1px', background: 'var(--color-border)', margin: '0.75rem 0' }} />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-bold)', color: 'var(--color-success)', background: 'var(--color-success-light)', padding: '0.2rem 0.6rem', borderRadius: 'var(--radius-full)' }}>
                    Remaining: 0.0 ✓
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Sticky Nav ── */}
      <div style={{ position: 'sticky', top: 'var(--navbar-height)', zIndex: 100, background: 'var(--color-surface)', borderBottom: '2px solid var(--color-border)', padding: '0.6rem 0' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' }}>
            {[
              { href: '#when-to-use',    label: 'When to Use'   },
              { href: '#how-to-use',     label: 'How to Use'    },
              { href: '#examples',       label: 'Examples'      },
              { href: '#quick-actions',  label: 'Quick Actions' },
              { href: '#troubleshooting', label: 'FAQ'          },
            ].map(({ href, label }) => (
              <a key={href} href={href} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-text-muted)', padding: '0.3rem 0.9rem', borderRadius: 'var(--radius-full)', border: '1px solid var(--color-border)', textDecoration: 'none', transition: 'color var(--transition-fast), border-color var(--transition-fast)' }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--color-primary)'; e.currentTarget.style.borderColor = 'var(--color-primary)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--color-text-muted)'; e.currentTarget.style.borderColor = 'var(--color-border)'; }}
              >{label}</a>
            ))}
          </div>
        </div>
      </div>

      {/* ── When to Use ── */}
      <section id="when-to-use" style={{ padding: '3.5rem 0', background: 'var(--color-surface)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <SectionHeading eyebrow="Guidance" title="When to Use Credit Distribution" />
          <div className="row g-4">
            <div className="col-md-6">
              <div style={{ background: 'var(--color-surface-raised)', borderRadius: 'var(--radius-md)', overflow: 'hidden', boxShadow: 'var(--shadow-card)', height: '100%', borderTop: '4px solid var(--color-success)' }}>
                <div style={{ padding: '1.25rem 1.5rem', background: `color-mix(in srgb, var(--color-success) 8%, var(--color-surface-raised))`, borderBottom: '1px solid var(--color-border)' }}>
                  <h5 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)', color: 'var(--color-text)', margin: 0 }}>
                    <span style={{ color: 'var(--color-success)', marginRight: '0.5rem' }}>✓</span>Use Credit Distribution When:
                  </h5>
                </div>
                <div style={{ padding: '1.5rem' }}>
                  <ul style={{ paddingLeft: '1.25rem', margin: '0 0 1.25rem' }}>
                    {[
                      'A class covers multiple distinct topics with separate credit amounts',
                      'State requirements specify different amounts per category',
                      'You need precise tracking of category-specific hours',
                      'Your certificate shows subcategory breakdowns',
                    ].map(item => (
                      <li key={item} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', marginBottom: '0.5rem', lineHeight: 1.55 }}>{item}</li>
                    ))}
                  </ul>
                  <div style={{ padding: '1rem', background: 'var(--color-surface-muted)', borderRadius: 'var(--radius-sm)', borderLeft: '3px solid var(--color-success)' }}>
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 0.4rem' }}>Example</p>
                    <code style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--color-text)', lineHeight: 1.8 }}>
                      NY CLE: "Ethics &amp; Diversity Training"<br />
                      Total: 3.5 credits<br />
                      - Ethics (Cybersecurity): 2.5<br />
                      - Diversity: 1.0
                    </code>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div style={{ background: 'var(--color-surface-raised)', borderRadius: 'var(--radius-md)', overflow: 'hidden', boxShadow: 'var(--shadow-card)', height: '100%', borderTop: '4px solid var(--color-accent-warm)' }}>
                <div style={{ padding: '1.25rem 1.5rem', background: `color-mix(in srgb, var(--color-accent-warm) 8%, var(--color-surface-raised))`, borderBottom: '1px solid var(--color-border)' }}>
                  <h5 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)', color: 'var(--color-text)', margin: 0 }}>
                    <span style={{ color: 'var(--color-danger)', marginRight: '0.5rem' }}>✗</span>Don't Use When:
                  </h5>
                </div>
                <div style={{ padding: '1.5rem' }}>
                  <ul style={{ paddingLeft: '1.25rem', margin: '0 0 1.25rem' }}>
                    {[
                      'Class applies fully to one category',
                      "You're okay with approximate tracking",
                      'Class counts toward any category requirement',
                    ].map(item => (
                      <li key={item} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', marginBottom: '0.5rem', lineHeight: 1.55 }}>{item}</li>
                    ))}
                  </ul>
                  <div style={{ padding: '1rem', background: 'var(--color-surface-muted)', borderRadius: 'var(--radius-sm)', borderLeft: '3px solid var(--color-accent-warm)', marginBottom: '1rem' }}>
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 0.4rem' }}>Example</p>
                    <code style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--color-text)', lineHeight: 1.8 }}>
                      CT CLE: "Contract Law Update"<br />
                      Total: 2.0 credits<br />
                      Category: General (full 2.0)
                    </code>
                  </div>
                  <Callout icon="bi-lightbulb" color="var(--color-accent)">
                    <strong>Tip:</strong> Use traditional checkbox mode for simple one-category classes!
                  </Callout>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── How to Use ── */}
      <section id="how-to-use" style={{ padding: '3.5rem 0', background: 'var(--color-surface-muted)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <SectionHeading eyebrow="Step-by-Step" title="How to Use — 4 Easy Steps" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {[
              {
                n: '1', color: 'var(--color-primary)', title: 'Navigate to Class from Cycle',
                content: (
                  <ol style={{ paddingLeft: '1.25rem', margin: 0 }}>
                    {['Go to "My Cycles"', 'Click on a cycle (e.g., "New York 2024–2026")', 'Find the class you want to categorize', <span key="4">Click the <InlineTag color="var(--color-primary)">Edit</InlineTag> button</span>].map((item, i) => (
                      <li key={i} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', marginBottom: '0.4rem', lineHeight: 1.55 }}>{item}</li>
                    ))}
                  </ol>
                ),
              },
              {
                n: '2', color: 'var(--color-accent)', title: 'Enable Distribution Mode',
                content: (
                  <div>
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', marginBottom: '0.75rem' }}>
                      In the <strong>"Select Categories for This Class"</strong> section:
                    </p>
                    <ol style={{ paddingLeft: '1.25rem', margin: '0 0 1rem' }}>
                      {[<span key="1">Find the toggle switch: <strong>"Distribute credits"</strong></span>, <span key="2">Turn it <InlineTag color="var(--color-accent)">ON</InlineTag> (teal)</span>, 'The category distribution panel appears'].map((item, i) => (
                        <li key={i} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', marginBottom: '0.4rem', lineHeight: 1.55 }}>{item}</li>
                      ))}
                    </ol>
                    <div style={{ padding: '0.75rem 1rem', background: 'var(--color-surface-muted)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{ width: '36px', height: '20px', borderRadius: 'var(--radius-full)', background: 'var(--color-accent)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 2px', flexShrink: 0 }}>
                        <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#fff' }} />
                      </div>
                      <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text)' }}>Distribute credits</span>
                    </div>
                  </div>
                ),
              },
              {
                n: '3', color: 'var(--color-accent-warm)', title: 'Distribute Credits (3 Options)',
                content: (
                  <div className="row g-3">
                    {[
                      { label: 'Option A: Quick Assign', accent: 'var(--color-primary)', items: ['Click Quick: All to General', 'All credits → GENERAL', '✓ Done!'] },
                      { label: 'Option B: Manual',       accent: 'var(--color-success)', items: ['Check category boxes', 'Enter credit amounts', 'Watch "remaining" counter', 'Must reach 0.0'] },
                      { label: 'Option C: Evenly',       accent: 'var(--color-accent)',  items: ['Select multiple categories', 'Click Distribute Evenly', 'Auto-splits equally'] },
                    ].map(opt => (
                      <div key={opt.label} className="col-md-4">
                        <div style={{ padding: '1rem', borderRadius: 'var(--radius-sm)', background: `color-mix(in srgb, ${opt.accent} 6%, var(--color-surface-raised))`, border: `1px solid color-mix(in srgb, ${opt.accent} 25%, transparent)`, height: '100%' }}>
                          <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: opt.accent, marginBottom: '0.6rem' }}>{opt.label}</p>
                          <ul style={{ paddingLeft: '1rem', margin: 0 }}>
                            {opt.items.map(item => (
                              <li key={item} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', color: 'var(--color-text)', marginBottom: '0.3rem', lineHeight: 1.5 }}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                ),
              },
              {
                n: '4', color: 'var(--color-success)', title: 'Save (Automatic!)',
                content: (
                  <div>
                    <ul style={{ paddingLeft: '1.25rem', margin: '0 0 1rem' }}>
                      {['Changes save automatically when you check/uncheck categories', 'Green success message confirms save', 'Credits now tracked separately per category'].map(item => (
                        <li key={item} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', marginBottom: '0.4rem', lineHeight: 1.55 }}>{item}</li>
                      ))}
                    </ul>
                    <Callout icon="bi-check-circle-fill" color="var(--color-success)">
                      <strong>Success!</strong> Class categories updated successfully.
                    </Callout>
                  </div>
                ),
              },
            ].map(step => (
              <div key={step.n} style={{ background: 'var(--color-surface-raised)', borderRadius: 'var(--radius-md)', overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
                <div style={{ padding: '1.5rem', background: `color-mix(in srgb, ${step.color} 6%, var(--color-surface-raised))`, borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
                  <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: step.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: `0 4px 16px color-mix(in srgb, ${step.color} 40%, transparent)` }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '4rem', color: '#fff', lineHeight: 1 }}>{step.n}</span>
                  </div>
                  <h5 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(1.5rem, 2.5vw, 2.25rem)', color: 'var(--color-text)', margin: 0, lineHeight: 1.2 }}>Step {step.n}: {step.title}</h5>
                </div>
                <div style={{ padding: '1.5rem' }}>{step.content}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Examples ── */}
      <section id="examples" style={{ padding: '3.5rem 0', background: 'var(--color-surface)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <SectionHeading eyebrow="Real-World" title="Examples by Profession" />

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2.5rem', borderBottom: '2px solid var(--color-border)', paddingBottom: '0' }}>
            {([
              { id: 'lawyers',    label: 'Attorneys (CLE)'   },
              { id: 'architects', label: 'Architects (LU)'   },
              { id: 'engineers',  label: 'Engineers (PDH)'   },
            ] as const).map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)',
                  padding: '0.6rem 1.25rem', border: 'none', borderRadius: 'var(--radius-sm) var(--radius-sm) 0 0',
                  cursor: 'pointer', marginBottom: '-2px', borderBottom: activeTab === tab.id ? '2px solid var(--color-primary)' : '2px solid transparent',
                  background: activeTab === tab.id ? 'var(--color-surface-raised)' : 'transparent',
                  color: activeTab === tab.id ? 'var(--color-primary)' : 'var(--color-text-muted)',
                  transition: 'color var(--transition-fast)',
                }}
              >{tab.label}</button>
            ))}
          </div>

          {/* Lawyers */}
          {activeTab === 'lawyers' && (
            <div className="row g-4">
              <ExampleCard title="NY — Mixed Ethics" accent="var(--color-prof-law)"
                classDetails={{ name: '"Cybersecurity & Bias Training 2024"', total: '3.5 CLE', cert: '2.5 hrs Cyber Ethics + 1.0 hr Diversity' }}
                distribution={[{ label: 'Ethics (Cybersecurity)', amount: '2.5' }, { label: 'Diversity', amount: '1.0' }]}
                result={['Ethics: +2.5 / 6 required', 'Diversity: +1.0 / 1 required ✓', 'Total: 3.5 / 24 required']}
              />
              <ExampleCard title="CT — Single Category" accent="var(--color-success)"
                classDetails={{ name: '"Professional Responsibility 2024"', total: '2.0 CLE', cert: 'Applies fully to Ethics' }}
                note={<Callout icon="bi-exclamation-triangle" color="var(--color-accent-warm)"><strong>Keep "Distribute credits" OFF</strong> — use traditional checkbox mode for single-category classes.</Callout>}
                result={['Ethics: +2.0 / 2 required ✓', 'Simpler & faster!']}
              />
              <ExampleCard title="NJ — Trust & Technology" accent="var(--color-accent)"
                classDetails={{ name: '"NJ Trust Accounting & Tech"', total: '2.0 CLE', cert: '1.0 hr Trust + 1.0 hr Practice Tech' }}
                distribution={[{ label: 'NJ Trust Accounting', amount: '1.0' }, { label: 'NJ Practice Technology', amount: '1.0' }]}
                result={['NJ Trust: +1.0 / 1 required ✓', 'NJ Practice: +1.0 / 16 required', 'Both categories credited!']}
              />
              <ExampleCard title="Multi-State — Same Class, Different States" accent="var(--color-text-muted)"
                classDetails={{ name: '"Ethics Training"', total: '4.0 hrs', cert: 'Licensed in NY + NJ' }}
                multiState={[
                  { state: 'Step 1 — Open your NY Cycle', color: 'var(--color-prof-law)', lines: ['Find this class → edit categories', 'Assign: Ethics (Cyber) 3.0 + Diversity 1.0', 'Save → NY cycle updates'] },
                  { state: 'Step 2 — Open your NJ Cycle', color: 'var(--color-success)',  lines: ['Find the same class → edit categories', 'Assign: Ethics 4.0 (NJ has no diversity req.)', 'Save → NJ cycle updates independently'] },
                ]}
                result={['One class, two cycles, two different distributions ✓', 'You work in each cycle separately — not simultaneously']}
              />
            </div>
          )}

          {/* Architects */}
          {activeTab === 'architects' && (
            <div className="row g-4">
              <ExampleCard title="CA Architect — HSW + Sustainability" accent="var(--color-prof-arch)"
                classDetails={{ name: '"Green Building Design 2024"', total: '8.0 LU', cert: '5.0 LU HSW + 3.0 LU Sustainability' }}
                distribution={[{ label: 'Health/Safety/Welfare', amount: '5.0' }, { label: 'Sustainability', amount: '3.0' }]}
                result={['HSW: +5.0 / 10 required', 'Sustainability: +3.0 / 5 required', 'Both requirements progressing!']}
              />
              <ExampleCard title="NY Architect — Accessibility + Code" accent="var(--color-success)"
                classDetails={{ name: '"ADA & Building Code Updates"', total: '6.0 LU', cert: '4.0 LU Accessibility + 2.0 LU Building Code' }}
                distribution={[{ label: 'Accessibility', amount: '4.0' }, { label: 'Building Code', amount: '2.0' }]}
                result={['Accessibility: +4.0 / 4 required ✓', 'Building Code: +2.0 / 12 required', 'Accessibility requirement met!']}
              />
            </div>
          )}

          {/* Engineers */}
          {activeTab === 'engineers' && (
            <div className="row g-4">
              <ExampleCard title="TX Engineer — Ethics + Technical" accent="var(--color-prof-pe)"
                classDetails={{ name: '"Engineering Ethics & Structural Updates"', total: '4.0 PDH', cert: '1.0 hr Ethics + 3.0 hrs Technical' }}
                distribution={[{ label: 'Ethics', amount: '1.0' }, { label: 'Technical', amount: '3.0' }]}
                result={['Ethics: +1.0 / 1 required ✓', 'Technical: +3.0 / 14 required', 'Ethics requirement met!']}
              />
              <ExampleCard title="FL Engineer — Professional Development" accent="var(--color-success)"
                classDetails={{ name: '"Project Management & Florida Laws"', total: '5.0 PDH', cert: '3.0 hrs PM + 2.0 hrs Florida Laws' }}
                distribution={[{ label: 'Project Management', amount: '3.0' }, { label: 'Florida Laws', amount: '2.0' }]}
                result={['Project Management: +3.0 / 10 required', 'Florida Laws: +2.0 / 2 required ✓', 'Laws requirement met!']}
              />
            </div>
          )}
        </div>
      </section>

      {/* ── Quick Actions ── */}
      <section id="quick-actions" style={{ padding: '3.5rem 0', background: 'var(--color-surface-muted)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <SectionHeading eyebrow="Reference" title="Quick Actions Guide" />
          <div className="row g-4">
            {[
              {
                icon: '→', label: 'Quick: All to General', accent: 'var(--color-primary)',
                description: 'Assigns all credits to GENERAL category.',
                bestFor: ['Non-specific classes', 'When you want speed', 'No category requirements'],
              },
              {
                icon: '÷', label: 'Distribute Evenly', accent: 'var(--color-accent)',
                description: 'Splits credits equally across selected categories.',
                bestFor: ['Multi-topic classes', 'Equal time per topic', 'Quick approximation'],
                note: '3.0 credits ÷ 3 categories = 1.0 each',
              },
              {
                icon: '✕', label: 'Clear All', accent: 'var(--color-danger)',
                description: 'Removes all selections and resets amounts.',
                bestFor: ['Starting over fresh', 'Fixing mistakes', 'Trying different distribution'],
              },
            ].map(action => (
              <div key={action.label} className="col-md-4">
                <div style={{ background: 'var(--color-surface-raised)', borderRadius: 'var(--radius-md)', overflow: 'hidden', boxShadow: 'var(--shadow-card)', height: '100%', borderTop: `4px solid ${action.accent}` }}>
                  <div style={{ padding: '1.75rem', textAlign: 'center', borderBottom: '1px solid var(--color-border)' }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: '4rem', color: action.accent, marginBottom: '0.75rem', lineHeight: 1 }}>{action.icon}</div>
                    <h5 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)', color: 'var(--color-text)', marginBottom: '0.5rem' }}>{action.label}</h5>
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text-muted)', margin: 0 }}>{action.description}</p>
                  </div>
                  <div style={{ padding: '1.25rem 1.5rem' }}>
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Best for:</p>
                    <ul style={{ paddingLeft: '1rem', margin: 0 }}>
                      {action.bestFor.map(item => (
                        <li key={item} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', marginBottom: '0.3rem' }}>{item}</li>
                      ))}
                    </ul>
                    {action.note && (
                      <p style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--color-text-muted)', marginTop: '0.75rem', marginBottom: 0 }}>{action.note}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Validation Rules ── */}
      <section id="validation" style={{ padding: '3.5rem 0', background: 'var(--color-surface)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <SectionHeading eyebrow="Reference" title="Validation Rules" />
          <div className="row g-4">
            <div className="col-md-6">
              <div style={{ background: 'var(--color-surface-raised)', borderRadius: 'var(--radius-md)', overflow: 'hidden', boxShadow: 'var(--shadow-card)', height: '100%', borderTop: '4px solid var(--color-success)' }}>
                <div style={{ padding: '1.25rem 1.5rem', background: `color-mix(in srgb, var(--color-success) 8%, var(--color-surface-raised))`, borderBottom: '1px solid var(--color-border)' }}>
                  <h5 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)', color: 'var(--color-text)', margin: 0 }}>
                    <span style={{ color: 'var(--color-success)', marginRight: '0.4rem' }}>✓</span> Valid Distribution
                  </h5>
                </div>
                <div style={{ padding: '1.5rem' }}>
                  <ul style={{ paddingLeft: '1.25rem', margin: '0 0 1.25rem' }}>
                    {['Sum equals total class credits', 'No category exceeds maximum', 'All meet their minimum'].map(item => (
                      <li key={item} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', marginBottom: '0.4rem' }}>{item}</li>
                    ))}
                  </ul>
                  <Callout icon="bi-check-circle-fill" color="var(--color-success)">
                    <strong>Success!</strong> Distribution is valid. Remaining: 0.0
                  </Callout>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div style={{ background: 'var(--color-surface-raised)', borderRadius: 'var(--radius-md)', overflow: 'hidden', boxShadow: 'var(--shadow-card)', height: '100%', borderTop: '4px solid var(--color-danger)' }}>
                <div style={{ padding: '1.25rem 1.5rem', background: `color-mix(in srgb, var(--color-danger) 8%, var(--color-surface-raised))`, borderBottom: '1px solid var(--color-border)' }}>
                  <h5 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)', color: 'var(--color-text)', margin: 0 }}>
                    <span style={{ color: 'var(--color-danger)', marginRight: '0.4rem' }}>✗</span> Invalid Distribution
                  </h5>
                </div>
                <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <Callout icon="bi-exclamation-triangle" color="var(--color-accent-warm)">
                    <small>Please assign the remaining 0.5 credits</small>
                  </Callout>
                  <Callout icon="bi-x-circle" color="var(--color-danger)">
                    <small>Total exceeds available credits by 1.0</small>
                  </Callout>
                  <Callout icon="bi-exclamation-triangle" color="var(--color-accent-warm)">
                    <small>Ethics requires minimum of 1.0 credits</small>
                  </Callout>
                  <Callout icon="bi-x-circle" color="var(--color-danger)">
                    <small>General allows maximum of 20.0 credits</small>
                  </Callout>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Tips ── */}
      <section id="tips" style={{ padding: '3.5rem 0', background: 'var(--color-surface-muted)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <SectionHeading eyebrow="Best Practices" title="Tips for Accurate Tracking" />
          <div className="row g-4">
            {[
              { icon: 'bi-file-earmark-check', title: 'Keep Certificates Handy',        body: 'Check your certificate for exact breakdowns. Match it precisely for audit compliance.' },
              { icon: 'bi-bar-chart-line',     title: 'Review Cycle Progress',          body: 'After distributing, check cycle progress bars to ensure credits appear in correct categories.' },
              { icon: 'bi-pencil-square',      title: 'You Can Change Anytime',         body: 'Edit the class again to adjust distribution. Previous values load automatically.' },
              { icon: 'bi-toggle-off',         title: 'Traditional Mode for Simple',   body: 'Keep toggle OFF for one-category classes. Faster and simpler!' },
              { icon: 'bi-sticky',             title: 'Use Notes Field',               body: 'Add breakdown notes in class memo: "2.5 Cyber + 1.0 Diversity per certificate"' },
              { icon: 'bi-percent',            title: 'Decimal Precision',             body: 'System supports decimals (0.5, 1.25, etc.) for exact certificate matching.' },
            ].map(tip => (
              <div key={tip.title} className="col-md-6 col-lg-4">
                <div style={{ background: 'var(--color-surface-raised)', borderRadius: 'var(--radius-md)', padding: '1.5rem', boxShadow: 'var(--shadow-card)', height: '100%', borderLeft: '3px solid var(--color-accent)' }}>
                  <i className={`bi ${tip.icon}`} style={{ fontSize: '2.75rem', color: 'var(--color-accent)', marginBottom: '1rem', display: 'block' }} />
                  <h5 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-md)', color: 'var(--color-text)', marginBottom: '0.5rem' }}>{tip.title}</h5>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text-muted)', margin: 0, lineHeight: 1.6 }}>{tip.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="troubleshooting" style={{ padding: '3.5rem 0', background: 'var(--color-surface)' }}>
        <div className="container" style={{ maxWidth: '800px', margin: '0 auto', padding: '0 1.5rem' }}>
          <SectionHeading eyebrow="FAQ" title="Frequently Asked Questions" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {[
              { id: 'faq1', q: 'Distribution toggle not showing?',
                a: <><p style={{ marginBottom: '0.5rem' }}>Toggle only appears when:</p><ul style={{ paddingLeft: '1.25rem', margin: 0 }}>{['Editing from a cycle context (via "My Cycles" page)', 'State has category requirements configured', 'Not in add mode (must be editing existing class)'].map(i => <li key={i} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', marginBottom: '0.3rem' }}>{i}</li>)}</ul></> },
              { id: 'faq2', q: "Categories don't match my certificate?",
                a: <><p style={{ marginBottom: '0.5rem' }}>Category names vary by state. Common mappings:</p><ul style={{ paddingLeft: '1.25rem', margin: '0 0 0.5rem' }}>{['Certificate: "Ethics (Technology)" → App: "Ethics (Cybersecurity)"', 'Certificate: "Bias Elimination" → App: "Diversity"', 'Certificate: "Professionalism" → App: "General"'].map(i => <li key={i} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', marginBottom: '0.3rem' }}>{i}</li>)}</ul><p style={{ margin: 0, fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)' }}>Choose the closest matching category in the app.</p></> },
              { id: 'faq3', q: "Can't save — validation error?",
                a: <><p style={{ marginBottom: '0.5rem' }}>Check these common issues:</p><ul style={{ paddingLeft: '1.25rem', margin: 0 }}>{['Sum must equal total credits (use calculator if needed)', 'No category can exceed its maximum', 'Each selected category must meet minimum', 'Look for yellow/red warning messages'].map(i => <li key={i} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', marginBottom: '0.3rem' }}>{i}</li>)}</ul></> },
              { id: 'faq4', q: 'Progress not updating after distribution?',
                a: <><p style={{ marginBottom: '0.5rem' }}>Try these steps:</p><ul style={{ paddingLeft: '1.25rem', margin: 0 }}>{['Wait 1–2 seconds for auto-save to complete', 'Refresh the cycle detail page', 'Verify class is marked "completed" (took checkbox)', 'Check for green success message after saving'].map(i => <li key={i} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', marginBottom: '0.3rem' }}>{i}</li>)}</ul></> },
              { id: 'faq5', q: 'Need to split across 5+ categories?',
                a: <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', margin: 0 }}>No limit! Select as many categories as needed and enter amounts for each. The system handles any number of categories.</p> },
              { id: 'faq6', q: 'Can I use this for multiple states?',
                a: <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', margin: 0 }}>Yes! Same class can have different distributions per state. Edit the class from each cycle separately and distribute according to that state's requirements.</p> },
              { id: 'faq7', q: 'What about audit preparation?',
                a: <><p style={{ marginBottom: '0.5rem' }}>For audits:</p><ul style={{ paddingLeft: '1.25rem', margin: 0 }}>{['Keep original certificates showing credit breakdowns', 'Use class notes to document distribution reasoning', 'Export cycle report for official records', 'Match certificate breakdowns exactly in the app'].map(i => <li key={i} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', marginBottom: '0.3rem' }}>{i}</li>)}</ul></> },
            ].map(item => (
              <div key={item.id} style={{ background: 'var(--color-surface-raised)', borderRadius: 'var(--radius-md)', overflow: 'hidden', boxShadow: 'var(--shadow-xs)', border: '1px solid var(--color-border)' }}>
                <button
                  onClick={() => toggleFaq(item.id)}
                  style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.1rem 1.5rem', background: 'none', border: 'none', cursor: 'pointer', gap: '1rem', textAlign: 'left' }}
                >
                  <span style={{ fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-base)', color: 'var(--color-text)' }}>{item.q}</span>
                  <i className={`bi bi-chevron-${openFaq === item.id ? 'up' : 'down'}`} style={{ color: 'var(--color-text-muted)', flexShrink: 0, fontSize: 'var(--fs-sm)' }} />
                </button>
                {openFaq === item.id && (
                  <div style={{ padding: '0 1.5rem 1.25rem', borderTop: '1px solid var(--color-border)' }}>
                    <div style={{ paddingTop: '1rem' }}>{item.a}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding: '5rem 0', background: 'var(--color-surface-muted)' }}>
        <div className="container" style={{ maxWidth: '760px', margin: '0 auto', padding: '0 1.5rem', textAlign: 'center' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(2rem, 4vw, 3.25rem)', color: 'var(--color-text)', marginBottom: '1rem', lineHeight: 1.1, letterSpacing: '-0.01em' }}>
            Ready to Try<br />
            <em style={{ color: 'var(--color-accent)', fontStyle: 'italic' }}>Credit Distribution?</em>
          </h2>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-md)', color: 'var(--color-text-muted)', marginBottom: '2.5rem', lineHeight: 1.7 }}>
            Start distributing credits across categories for precise compliance tracking.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            <Link to="/cycles"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.875rem 2rem', background: 'var(--color-primary)', color: '#fff', fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-base)', borderRadius: 'var(--radius-md)', textDecoration: 'none', transition: 'background var(--transition-base), transform var(--transition-base)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-primary-hover)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--color-primary)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              Go to My Cycles <i className="bi bi-arrow-right" />
            </Link>
            <Link to="/how-it-works"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.875rem 2rem', background: 'transparent', color: 'var(--color-text)', fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-medium)', fontSize: 'var(--fs-base)', borderRadius: 'var(--radius-md)', textDecoration: 'none', border: '1px solid var(--color-border-strong)', transition: 'border-color var(--transition-base)' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-text)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border-strong)'; }}
            >
              Back to How It Works
            </Link>
          </div>
        </div>
      </section>

      {/* ── Support ── */}
      <section style={{ padding: '3rem 0', background: 'var(--color-surface)', borderTop: '1px solid var(--color-border)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <div className="row">
            <div className="col-md-6">
              <p style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 'var(--fs-md)', color: 'var(--color-text)', marginBottom: '0.4rem' }}>Need Help?</p>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text-muted)', margin: 0 }}>
                Contact <Link to="/contact" style={{ color: 'var(--color-accent)' }}>support</Link> if you have questions about credit distribution.
              </p>
            </div>
            <div className="col-md-6 mt-4 mt-md-0">
              <p style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 'var(--fs-md)', color: 'var(--color-text)', marginBottom: '0.4rem' }}>More Resources</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                {[
                  { to: '/how-it-works', label: 'Introduction — How It Works' },
                  { to: '/dashboard',    label: 'Dashboard'                   },
                  { to: '/classes',      label: 'My Classes'                  },
                ].map(({ to, label }) => (
                  <Link key={to} to={to} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-accent)', textDecoration: 'none' }}
                    onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                    onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                  >{label}</Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────────

const SectionHeading = ({ eyebrow, title }: { eyebrow: string; title: string }) => (
  <div style={{ marginBottom: '1.75rem' }}>
    <p style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-bold)', color: 'var(--color-accent)', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: '0.4rem' }}>{eyebrow}</p>
    <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', color: 'var(--color-text)', margin: 0, lineHeight: 1.15, letterSpacing: '-0.01em' }}>{title}</h2>
  </div>
);

const Callout = ({ icon, color, children }: { icon: string; color: string; children: React.ReactNode }) => (
  <div style={{ padding: '0.875rem 1.1rem', background: `color-mix(in srgb, ${color} 8%, var(--color-surface-raised))`, border: `1px solid color-mix(in srgb, ${color} 30%, transparent)`, borderLeft: `4px solid ${color}`, borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'flex-start', gap: '0.65rem' }}>
    <i className={`bi ${icon}`} style={{ color, fontSize: '1.5rem', marginTop: 0, flexShrink: 0 }} />
    <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', lineHeight: 1.6 }}>{children}</span>
  </div>
);

const InlineTag = ({ color, children }: { color: string; children: React.ReactNode }) => (
  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-bold)', color: '#fff', background: color, padding: '0.1em 0.5em', borderRadius: 'var(--radius-xs)' }}>{children}</span>
);

const DistRow = ({ label, amount, color }: { label: string; amount: string; color: string }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
    <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)' }}>{label}</span>
    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-bold)', color: '#fff', background: color, padding: '0.15em 0.6em', borderRadius: 'var(--radius-xs)' }}>{amount}</span>
  </div>
);

interface ExampleCardProps {
  title: string;
  accent: string;
  classDetails: { name: string; total: string; cert: string };
  distribution?: { label: string; amount: string }[];
  note?: React.ReactNode;
  multiState?: { state: string; color: string; lines: string[] }[];
  result: string[];
}

const ExampleCard = ({ title, accent, classDetails, distribution, note, multiState, result }: ExampleCardProps) => (
  <div className="col-lg-6">
    <div style={{ background: 'var(--color-surface-raised)', borderRadius: 'var(--radius-md)', overflow: 'hidden', boxShadow: 'var(--shadow-card)', height: '100%', borderTop: `4px solid ${accent}` }}>
      <div style={{ padding: '1.1rem 1.5rem', background: `color-mix(in srgb, ${accent} 8%, var(--color-surface-raised))`, borderBottom: '1px solid var(--color-border)' }}>
        <h5 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-md)', color: 'var(--color-text)', margin: 0 }}>{title}</h5>
      </div>
      <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>Class Details</p>
          <ul style={{ paddingLeft: '1.1rem', margin: 0 }}>
            <li style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', marginBottom: '0.25rem' }}><strong>Name:</strong> {classDetails.name}</li>
            <li style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', marginBottom: '0.25rem' }}><strong>Total:</strong> {classDetails.total}</li>
            <li style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)' }}><strong>Certificate:</strong> {classDetails.cert}</li>
          </ul>
        </div>

        {distribution && (
          <div>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>Distribution</p>
            <div style={{ padding: '1rem', background: 'var(--color-surface-muted)', borderRadius: 'var(--radius-sm)' }}>
              {distribution.map(d => (
                <DistRow key={d.label} label={d.label} amount={d.amount} color={accent} />
              ))}
              <div style={{ height: '1px', background: 'var(--color-border)', margin: '0.5rem 0' }} />
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-bold)', color: 'var(--color-success)', background: 'var(--color-success-light)', padding: '0.15em 0.6em', borderRadius: 'var(--radius-full)' }}>Remaining: 0.0 ✓</span>
              </div>
            </div>
          </div>
        )}

        {note && <div>{note}</div>}

        {multiState && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {multiState.map(ms => (
              <div key={ms.state} style={{ padding: '0.75rem 1rem', background: 'var(--color-surface-muted)', borderRadius: 'var(--radius-sm)', borderLeft: `3px solid ${ms.color}` }}>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', color: ms.color, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.35rem' }}>{ms.state}</p>
                {ms.lines.map(line => (
                  <p key={line} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', margin: 0, lineHeight: 1.5 }}>· {line}</p>
                ))}
              </div>
            ))}
          </div>
        )}

        <Callout icon="bi-graph-up" color={accent}>
          <strong>Result:</strong><br />
          {result.map((r, i) => <span key={i} style={{ display: 'block' }}>· {r}</span>)}
        </Callout>
      </div>
    </div>
  </div>
);

export default CreditDistributionGuide;
